import React from 'react';
import { Box, Menu, MenuItem, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStorefrontContext } from 'context/StorefrontContext';
import { NavItem } from 'types/storefront';
import { ROUTES } from 'config/routes';

const defaultNav: NavItem[] = [
  { labelKey: 'nav.catalog', url: ROUTES.CATALOG, order: 1 },
  { labelKey: 'nav.promotions', url: ROUTES.PROMOTIONS, order: 2 },
  { labelKey: 'nav.news', url: ROUTES.NEWS, order: 3 },
  { labelKey: 'nav.brands', url: ROUTES.BRANDS, order: 4 },
];

interface MegaMenuProps {
  uppercase?: boolean;
}

const MegaMenuItem: React.FC<{ item: NavItem; uppercase: boolean }> = ({ item, uppercase }) => {
  const { t } = useTranslation('common');
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
  const label = item.label || (item.labelKey ? t(item.labelKey) : item.url);
  const buttonSx = uppercase
    ? {
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase' as const,
        whiteSpace: 'nowrap' as const,
        color: 'text.primary',
      }
    : undefined;

  if (!item.children?.length) {
    return (
      <Button component={RouterLink} to={item.url} color="inherit" sx={buttonSx}>
        {label}
      </Button>
    );
  }

  return (
    <Box>
      <Button onClick={(e) => setAnchor(e.currentTarget)} sx={buttonSx}>
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {item.children.map((child) => (
          <MenuItem
            key={`${child.url}-${child.order ?? child.label}`}
            component={RouterLink}
            to={child.url}
            onClick={() => setAnchor(null)}
          >
            {child.label || (child.labelKey ? t(child.labelKey) : child.url)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const MegaMenu: React.FC<MegaMenuProps> = ({ uppercase = false }) => {
  const { navigation, features } = useStorefrontContext();

  const items = navigation.header.length > 0 ? navigation.header : defaultNav;

  const filtered = items.filter((item) => {
    if (item.url.includes('promociones') && !features.promotionsEnabled) return false;
    if (item.url.includes('marcas') && !features.brandsEnabled) return false;
    return true;
  });

  return (
    <Box display="flex" gap={1} flexWrap={uppercase ? 'nowrap' : 'wrap'}>
      {filtered.map((item, index) => (
        <MegaMenuItem key={`${item.url}-${item.order ?? index}`} item={item} uppercase={uppercase} />
      ))}
    </Box>
  );
};

export default MegaMenu;
